import React from 'react';
import { Box, Button, Card, Chip, Stack, Typography, useTheme } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

/**
 * O bloco que não pode ser calculado — uma superfície FECHADA INTEIRA.
 *
 * Duas decisões que este componente materializa:
 *
 * 1. **Nunca meio bloco.** Um card cinza no meio de outros preenchidos faz o
 *    usuário concluir que o produto está quebrado. Um bloco fechado inteiro,
 *    com hachura e cadeado, diz que falta dado dele — que é a verdade.
 *
 * 2. **O CTA nomeia os indicadores.** "Informe o balanço" não convence
 *    ninguém; "5 campos destravam Liquidez Corrente, Liquidez Seca, Ciclo
 *    Financeiro e mais 4" convence. A lista vem do catálogo, nunca da mão.
 */
/**
 * O bloco que ainda não tem dado — e o que ele passa a mostrar quando tiver.
 *
 * Ele dizia o que falta e parava aí. Falta é custo; o que convence alguém a
 * pagar o custo é o ganho, dito com o nome dos indicadores que aparecem do
 * outro lado. "Informe 5 campos" é uma cobrança. "Informe 5 campos e você
 * passa a ver em quantos dias o seu dinheiro volta" é uma proposta.
 */
const BlocoBloqueado = ({ titulo, pergunta, chamada, campos = [], bloqueados = [], onDestravar }) => {
  const theme = useTheme();
  const escuro = theme.palette.mode === 'dark';

  // Hachura leve: "não informado" tem tratamento visual próprio, distinto de
  // zero e distinto de vazio. Sem cor de saúde — ausência não é diagnóstico.
  const hachura = `repeating-linear-gradient(135deg, transparent, transparent 7px, ${
    escuro ? 'rgba(255,255,255,0.035)' : 'rgba(17,24,39,0.035)'
  } 7px, ${escuro ? 'rgba(255,255,255,0.035)' : 'rgba(17,24,39,0.035)'} 8px)`;

  return (
    <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 }, backgroundImage: hachura }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
        <LockOutlinedIcon fontSize="small" sx={{ color: 'text.disabled', mt: 0.5 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" component="h2" sx={{ lineHeight: 1.25 }}>
            {titulo}
          </Typography>
          {pergunta && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '65ch' }}>
              {pergunta}
            </Typography>
          )}
        </Box>
      </Stack>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {chamada}
      </Typography>

      {campos.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: '75ch' }}>
          Campos que faltam: {campos.join(', ')}.
        </Typography>
      )}

      {bloqueados.length > 0 && (
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}
          aria-label="Indicadores bloqueados neste bloco"
        >
          {bloqueados.map((nome) => (
            <Chip key={nome} label={nome} size="small" variant="outlined" />
          ))}
        </Box>
      )}

      {onDestravar && (
        <Button variant="contained" onClick={onDestravar}>
          Informar o balanço
        </Button>
      )}
    </Card>
  );
};

export default BlocoBloqueado;
